import { z } from 'zod';
import { id, text } from './common.mjs';
import { nativePresentationFields, BOX_SPACING } from './presentation.mjs';
import { ManagedImageSlot } from '../images/values.mjs';
import { ManagedImageProjection } from '../images/projection.mjs';

// Keep the legacy reference's exact codec/bytes. The new alternative uses
// INS's sole managed slot codec; neither representation contains a URL.
export const nativeImageNodeSchema = z
  .object({
    id,
    kind: z.literal('image'),
    imageId: id
      .optional()
      .describe(
        'Legacy saved-image alternative. For an image captured in this Chat, use media:{state:"ready",imageId:<capture reply imageId>} instead; omit this top-level imageId. Never supply both alternatives.',
      ),
    media: ManagedImageSlot.optional().describe(
      'For an image captured in this Chat, use {state:"ready",imageId:<capture reply imageId>} and omit top-level imageId. Copy its actual sourceOperationId/captureOperationId into save_artifact.mediaSelection; these are separate source lookup names, not image IDs.',
    ),
    alt: text(1000),
    caption: text(1000).optional(),
    ...nativePresentationFields({ colours: ['background', 'border'], spacing: BOX_SPACING }),
  })
  .strict()
  .superRefine((value, ctx) => {
    if ((value.imageId !== undefined) === (value.media !== undefined))
      ctx.addIssue({ code: 'custom', message: 'image_reference_requires_one_alternative' });
  })
  .describe(
    'Image block requires exactly one alternative: media or legacy imageId, never both. New Chat captures use media:{state:"ready",imageId:<capture reply imageId>} plus the capture reply pair in save_artifact.mediaSelection. The legacy alternative remains readable for existing saved content.',
  );

/** Matches the immutable authored reference, including truthful historical pending state. */
export function assertNativeImageProjection(node, projection) {
  const image = nativeImageNodeSchema.parse(node);
  projection = ManagedImageProjection.parse(projection);
  if (projection.state === 'refused') return image;
  const reference = image.media ?? { state: 'ready', imageId: image.imageId };
  if (reference.state === 'ready') {
    if (
      !['ready', 'expired', 'withdrawn'].includes(projection.state) ||
      projection.imageId !== reference.imageId
    )
      throw new TypeError('image_projection_reference_mismatch');
  } else if (reference.state === 'capturing') {
    if (
      projection.state !== 'capturing' ||
      projection.captureOperationRef !== reference.captureOperationRef
    )
      throw new TypeError('image_projection_reference_mismatch');
  } else if (projection.state !== reference.state) {
    throw new TypeError('image_projection_reference_mismatch');
  }
  return image;
}
